import type { ClientUiPublic } from '../api/platformConfig'
import type { ComicProjectCreative } from './comicCreativeBible'
import { normalizeComicProjectCreative } from './comicCreativeBible'
import {
  type ComicPageLayoutTemplate,
  getPageLayoutOption,
  normalizePageLayout,
  pageMaxPanels,
} from './comicPageLayout'
import {
  appendPanelToPageIds,
  collectOverflowPanelIds,
  createEmptyPage,
  distributePanelIdsToPages,
  ensureEpisodePages,
  movePanelToPageSlot,
  pickPageForNewPanel,
  reindexComicPages,
  reindexPagesAfterPanelDelete,
  removePanelIdFromPages,
  renumberEpisodePanelOrders,
  reorderPanelOnPage,
  seedMissingPagePanels,
  updateEpisodePages,
} from './comicPages'
import { syncPanelImageFromSelection } from './comicPanelCandidates'
import {
  type ComicProjectSettings,
} from './comicProjectSettings'
import type { ComicEpisode, ComicEpisodeEditPatch, ComicPage, ComicPanel, ComicProject } from './comicTypes'
import { randomUuid } from './randomUuid'

const emptyPanel = (): ComicPanel => ({
  id: '',
  order: 0,
  script: '',
  dialogue: '',
  narration: '',
  imageUrl: null,
  selectedCandidateId: null,
  candidates: [],
  creationTaskId: null,
  status: 'draft',
})

function mapEpisode(ep: ComicEpisode): ComicEpisode {
  return seedMissingPagePanels(ensureEpisodePages(ep))
}

export function getPrimaryEpisode(project: ComicProject): ComicEpisode {
  const raw =
    project.episodes[0] ?? {
      id: randomUuid(),
      index: 1,
      title: '第 1 话',
      panels: [],
      pages: [],
    }
  return mapEpisode(raw)
}

/** 当前编辑话；episodeId 无效时回退第 1 话 */
export function getActiveEpisode(project: ComicProject, episodeId: string | null): ComicEpisode {
  if (episodeId) {
    const found = project.episodes.find((e) => e.id === episodeId)
    if (found) {
      return mapEpisode(found)
    }
  }
  return getPrimaryEpisode(project)
}

export function withEpisodeCount(project: ComicProject): ComicProject {
  return { ...project, episodeCount: project.episodes.length }
}

export function addEpisodeToProject(
  project: ComicProject,
  _clientUi: ClientUiPublic,
  _options?: { copySettingsFromEpisodeId?: string | null },
): { project: ComicProject; episodeId: string } {
  const nextIndex =
    project.episodes.length > 0 ? Math.max(...project.episodes.map((e) => e.index)) + 1 : 1
  const episode: ComicEpisode = ensureEpisodePages({
    id: randomUuid(),
    index: nextIndex,
    title: `第 ${nextIndex} 话`,
    panels: [],
    pages: [],
    createdAt: new Date().toISOString(),
  })
  const pages =
    episode.pages.length > 0 ? episode.pages : [createEmptyPage(1)]
  const normalized = { ...episode, pages }
  return {
    project: withEpisodeCount({
      ...project,
      episodes: [...project.episodes.map(mapEpisode), normalized],
    }),
    episodeId: normalized.id,
  }
}

export function removeEpisodeFromProject(project: ComicProject, episodeId: string): ComicProject | null {
  if (project.episodes.length <= 1) return null
  const reindexed = project.episodes
    .filter((e) => e.id !== episodeId)
    .map(mapEpisode)
    .map((ep, i) => ({ ...ep, index: i + 1 }))
  return withEpisodeCount({ ...project, episodes: reindexed })
}

export function editEpisodeInProject(
  project: ComicProject,
  episodeId: string,
  patch: ComicEpisodeEditPatch,
): ComicProject {
  const nextTitle = patch.title.trim()
  if (!nextTitle) return project
  const nextSummary = patch.summary?.trim() || undefined
  const nextPlotAdvance = patch.plotAdvance?.trim() || undefined
  return withEpisodeCount({
    ...project,
    episodes: project.episodes.map((ep) =>
      ep.id === episodeId
        ? {
            ...mapEpisode(ep),
            title: nextTitle,
            summary: nextSummary,
            plotAdvance: nextPlotAdvance,
          }
        : mapEpisode(ep),
    ),
  })
}

/** @deprecated 使用 {@link editEpisodeInProject} */
export function renameEpisodeInProject(
  project: ComicProject,
  episodeId: string,
  title: string,
): ComicProject {
  return editEpisodeInProject(project, episodeId, { title })
}

export function reorderEpisodesInProject(
  project: ComicProject,
  sourceEpisodeId: string,
  targetEpisodeId: string,
): ComicProject {
  if (sourceEpisodeId === targetEpisodeId) return project
  const episodes = project.episodes.map(mapEpisode)
  const from = episodes.findIndex((ep) => ep.id === sourceEpisodeId)
  const to = episodes.findIndex((ep) => ep.id === targetEpisodeId)
  if (from < 0 || to < 0) return project
  const moved = episodes.splice(from, 1)[0]
  episodes.splice(to, 0, moved)
  return withEpisodeCount({
    ...project,
    episodes: episodes.map((ep, index) => ({ ...ep, index: index + 1 })),
  })
}

export function mirrorProjectSettingsFromPrimary(
  project: ComicProject,
  _clientUi: ClientUiPublic,
): ComicProject {
  return project
}

export function addPageToEpisode(
  project: ComicProject,
  episodeId: string,
  layout?: ComicPageLayoutTemplate,
): { project: ComicProject; pageId: string } {
  const episodes = project.episodes.map((ep) => {
    if (ep.id !== episodeId) return mapEpisode(ep)
    const normalized = mapEpisode(ep)
    const pageLayout = normalizePageLayout(layout ?? normalized.pages.at(-1)?.layout)
    const { pages: trimmed, spill: overflow } = collectOverflowPanelIds(normalized.pages)
    const newPage = createEmptyPage(trimmed.length + 1, pageLayout)
    const maxNew = pageMaxPanels(newPage.layout)
    newPage.panelIds = overflow.splice(0, maxNew)
    const pages: ComicPage[] = [...trimmed, newPage]
    if (overflow.length > 0) {
      const last = pages.at(-1)!
      pages[pages.length - 1] = { ...last, panelIds: [...last.panelIds, ...overflow] }
    }
    return renumberEpisodePanelOrders({ ...normalized, pages: reindexComicPages(pages) })
  })
  const ep = episodes.find((e) => e.id === episodeId)
  const pageId = ep?.pages.at(-1)?.id ?? ''
  return { project: { ...project, episodes }, pageId }
}

export function setPageLayoutInEpisode(
  project: ComicProject,
  episodeId: string,
  pageId: string,
  layout: ComicPageLayoutTemplate,
): ComicProject {
  const nextLayout = normalizePageLayout(layout)
  return {
    ...project,
    episodes: project.episodes.map((ep) => {
      if (ep.id !== episodeId) return mapEpisode(ep)
      const normalized = mapEpisode(ep)
      return updateEpisodePages(normalized, (pages) =>
        pages.map((page) => {
          if (page.id !== pageId) return page
          const trimmed =
            page.panelIds.length > getPageLayoutOption(nextLayout).maxPanels
              ? page.panelIds.slice(0, getPageLayoutOption(nextLayout).maxPanels)
              : page.panelIds
          return { ...page, layout: nextLayout, panelIds: trimmed }
        }),
      )
    }),
  }
}

export function addPanelToProject(
  project: ComicProject,
  episodeId: string,
  options?: { pageId?: string | null },
): { project: ComicProject; panel: ComicPanel } {
  let createdPanel: ComicPanel | null = null
  const episodes = project.episodes.map((ep) => {
    if (ep.id !== episodeId) return mapEpisode(ep)
    const normalized = mapEpisode(ep)
    const order =
      normalized.panels.length > 0 ? Math.max(...normalized.panels.map((p) => p.order)) + 1 : 1
    const panel: ComicPanel = {
      id: randomUuid(),
      order,
      script: '',
      dialogue: '',
      narration: '',
      imageUrl: null,
      selectedCandidateId: null,
      candidates: [],
      creationTaskId: null,
      status: 'draft',
    }
    createdPanel = panel

    const targetPage = pickPageForNewPanel(normalized, options?.pageId)
    let pages = normalized.pages
    const exists = pages.some((p) => p.id === targetPage.id)
    if (!exists) {
      pages = [...pages, targetPage]
    }
    pages = pages.map((p) => (p.id === targetPage.id ? appendPanelToPageIds(p, panel.id) : p))

    const renumbered = renumberEpisodePanelOrders({ ...normalized, panels: [...normalized.panels, panel], pages })
    return renumbered
  })

  const panel = createdPanel ?? emptyPanel()
  return { project: { ...project, episodes }, panel }
}

export function updatePageInEpisode(
  project: ComicProject,
  episodeId: string,
  pageId: string,
  patch: Partial<ComicPage>,
): ComicProject {
  return {
    ...project,
    episodes: project.episodes.map((ep) => {
      if (ep.id !== episodeId) return mapEpisode(ep)
      const normalized = mapEpisode(ep)
      return {
        ...normalized,
        pages: normalized.pages.map((p) => (p.id === pageId ? { ...p, ...patch } : p)),
      }
    }),
  }
}

export function updatePanelInProject(
  project: ComicProject,
  episodeId: string,
  panelId: string,
  patch: Partial<
    Pick<
      ComicPanel,
      | 'script'
      | 'dialogue'
      | 'narration'
      | 'referenceImageUrls'
      | 'characterIds'
      | 'characterLines'
      | 'imageUrl'
      | 'selectedCandidateId'
      | 'candidates'
      | 'creationTaskId'
      | 'status'
      | 'errorMessage'
    >
  >,
): ComicProject {
  return {
    ...project,
    episodes: project.episodes.map((ep) => {
      if (ep.id !== episodeId) return mapEpisode(ep)
      const normalized = mapEpisode(ep)
      return {
        ...normalized,
        panels: normalized.panels.map((p) => {
          if (p.id !== panelId) return p
          const merged = { ...p, ...patch }
          return syncPanelImageFromSelection(merged)
        }),
      }
    }),
  }
}

export function removePanelFromProject(
  project: ComicProject,
  episodeId: string,
  panelId: string,
): ComicProject {
  return {
    ...project,
    episodes: project.episodes.map((ep) => {
      if (ep.id !== episodeId) return mapEpisode(ep)
      const normalized = mapEpisode(ep)
      const pages = reindexPagesAfterPanelDelete(removePanelIdFromPages(normalized.pages, panelId))
      return renumberEpisodePanelOrders({
        ...normalized,
        panels: normalized.panels.filter((p) => p.id !== panelId),
        pages: pages.length > 0 ? pages : [createEmptyPage(1)],
      })
    }),
  }
}

export function setProjectCoverIfEmpty(project: ComicProject, coverUrl: string): ComicProject {
  if (project.coverUrl) return project
  return { ...project, coverUrl }
}

export function setProjectCreative(
  project: ComicProject,
  creative: ComicProjectCreative,
  _clientUi: ClientUiPublic,
): ComicProject {
  const normalized = normalizeComicProjectCreative({ ...project.creative, ...creative })
  return { ...project, creative: normalized }
}

export function setEpisodeSettings(
  project: ComicProject,
  _episodeId: string,
  settings: ComicProjectSettings,
): ComicProject {
  return {
    ...project,
    settings,
    episodes: project.episodes.map(mapEpisode),
  }
}

export function setProjectSettings(project: ComicProject, settings: ComicProjectSettings): ComicProject {
  return setEpisodeSettings(project, '', settings)
}

export function removePageFromEpisode(
  project: ComicProject,
  episodeId: string,
  pageId: string,
): { project: ComicProject; nextPageId: string | null } | null {
  const targetEp = project.episodes.find((e) => e.id === episodeId)
  if (!targetEp) return null
  const normalized = mapEpisode(targetEp)
  if (normalized.pages.length <= 1) return null
  const deleteIdx = normalized.pages.findIndex((p) => p.id === pageId)
  if (deleteIdx < 0) return null

  const target = normalized.pages[deleteIdx]!
  const orphaned = [...target.panelIds]
  let pages = normalized.pages.filter((p) => p.id !== pageId)
  const { pages: distributed, spill } = distributePanelIdsToPages(pages, orphaned, deleteIdx)
  pages = distributed
  if (spill.length > 0) {
    const last = pages.at(-1)!
    pages[pages.length - 1] = { ...last, panelIds: [...last.panelIds, ...spill] }
  }
  const updatedEpisode = renumberEpisodePanelOrders({
    ...normalized,
    pages: reindexComicPages(pages),
  })
  const episodes = project.episodes.map((ep) =>
    ep.id === episodeId ? updatedEpisode : mapEpisode(ep),
  )
  const nextPageId =
    updatedEpisode.pages[Math.min(deleteIdx, updatedEpisode.pages.length - 1)]?.id ??
    updatedEpisode.pages[0]?.id ??
    null
  return { project: withEpisodeCount({ ...project, episodes }), nextPageId }
}

export function reorderPanelInEpisode(
  project: ComicProject,
  episodeId: string,
  pageId: string,
  panelId: string,
  direction: 'up' | 'down',
): ComicProject {
  return {
    ...project,
    episodes: project.episodes.map((ep) => {
      if (ep.id !== episodeId) return mapEpisode(ep)
      const normalized = mapEpisode(ep)
      const next = reorderPanelOnPage(normalized, pageId, panelId, direction)
      return next ?? normalized
    }),
  }
}

export function movePanelToPageInEpisode(
  project: ComicProject,
  episodeId: string,
  panelId: string,
  targetPageId: string,
  insertIndex: number,
): ComicProject | null {
  const ep = project.episodes.find((e) => e.id === episodeId)
  if (!ep) return null
  const normalized = mapEpisode(ep)
  const next = movePanelToPageSlot(normalized, panelId, targetPageId, insertIndex)
  if (!next) return null
  return {
    ...project,
    episodes: project.episodes.map((e) => (e.id === episodeId ? next : mapEpisode(e))),
  }
}

/** 按顺序将多个分镜迁入目标页末尾 */
export function movePanelsToPageInEpisode(
  project: ComicProject,
  episodeId: string,
  panelIds: string[],
  targetPageId: string,
): ComicProject | null {
  let working: ComicProject | null = project
  for (const panelId of panelIds) {
    if (!working) return null
    const ep = working.episodes.find((e) => e.id === episodeId)
    const page = ep?.pages.find((p) => p.id === targetPageId)
    const insertIndex = page
      ? page.panelIds.filter((id) => id !== panelId).length
      : 0
    working = movePanelToPageInEpisode(working, episodeId, panelId, targetPageId, insertIndex)
  }
  return working
}
